import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import RecruiterLayout from '@/components/RecruiterLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ViewToggle } from '@/components/ui/view-toggle';
import { Loader2, Plus, Edit, Trash2, Building2, MapPin, Globe, Linkedin, Users, Search, TrendingUp, TrendingDown, Filter, ChevronRight, Target, Sparkles, Activity } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import CompanyForm from '@/components/CompanyForm';
import { usePermissions } from '@/hooks/usePermissions';

const CRM_STATUS_COLORS: Record<string, string> = {
  not_prospect: 'bg-slate-500/10 text-slate-600 border-slate-500/20',
  lead: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  prospect: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  client: 'bg-green-500/10 text-green-600 border-green-500/20',
  on_hold: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  lost: 'bg-red-500/10 text-red-600 border-red-500/20',
};

const CATEGORY_LABELS: Record<string, string> = {
  services: 'Services',
  staffing: 'Staffing',
  end_client: 'End Client',
  unknown: 'Unknown',
  recruiter: 'Recruiter',
  it_company: 'IT Company',
  staff_augmentor: 'Staff Augmentor',
  consulting: 'Consulting',
};

interface CompanyWithCounts {
  id: string;
  name: string;
  category: string;
  location: string | null;
  employee_count_estimate: number | null;
  crm_status: string | null;
  website_url: string | null;
  linkedin_url: string | null;
  primary_location: string | null;
  secondary_locations: string[] | null;
  candidate_count: number;
}

const CompanyManagement = () => {
  const [companies, setCompanies] = useState<CompanyWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [view, setView] = useState<'card' | 'list'>('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState('companies');
  
  // Skill search state
  const [skills, setSkills] = useState<any[]>([]);
  const [selectedSkill, setSelectedSkill] = useState<string>('');
  const [skillCompanies, setSkillCompanies] = useState<any[]>([]);
  const [skillLocation, setSkillLocation] = useState<string>('');
  const [skillLoading, setSkillLoading] = useState(false);
  const [skillTypeFilter, setSkillTypeFilter] = useState<string>('all');
  const [skillStats, setSkillStats] = useState<any[]>([]);

  // Talent movement state
  const [timeWindow, setTimeWindow] = useState('6');
  const [outboundCompanies, setOutboundCompanies] = useState<any[]>([]);
  const [trendingSkills, setTrendingSkills] = useState<any[]>([]);
  const [movementLoading, setMovementLoading] = useState(false);
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const { can } = usePermissions();

  useEffect(() => {
    fetchCompanies();
    fetchSkills();
    fetchSkillStats();
    fetchTalentMovement();
  }, []);

  useEffect(() => {
    if (selectedSkill) {
      searchCompaniesWithSkill();
    }
  }, [selectedSkill, skillLocation]);

  useEffect(() => {
    if (activeTab === 'talent-movement') {
      fetchTalentMovement();
    }
  }, [timeWindow]);

  const fetchCompanies = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .order('name', { ascending: true });
      if (error) throw error;

      const companiesWithCounts = await Promise.all(
        (data || []).map(async (company) => {
          const { count } = await supabase
            .from('candidate_experience')
            .select('*', { count: 'exact', head: true })
            .eq('company_id', company.id);

          return {
            ...company,
            candidate_count: count || 0,
          };
        })
      );

      setCompanies(companiesWithCounts);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchSkills = async () => {
    const { data } = await supabase.from('skills').select('*').order('name');
    setSkills(data || []);
  };

  const fetchSkillStats = async () => {
    const { data } = await supabase
      .from('candidate_skills')
      .select('skill_id, skills(id, name, type)');

    const skillMap = new Map();
    data?.forEach(cs => {
      if (!cs.skills) return;
      const key = cs.skill_id;
      if (!skillMap.has(key)) {
        skillMap.set(key, { ...cs.skills, count: 0 });
      }
      skillMap.get(key).count++;
    });

    setSkillStats(
      Array.from(skillMap.values())
        .sort((a, b) => b.count - a.count)
        .slice(0, 12)
    );
  };

  const fetchTalentMovement = async () => {
    setMovementLoading(true);
    try {
      const monthsAgo = new Date();
      monthsAgo.setMonth(monthsAgo.getMonth() - parseInt(timeWindow));

      // Get companies with high outbound flow
      const { data: experiences } = await supabase
        .from('candidate_experience')
        .select('company_id, end_date, companies(id, name, location, category)')
        .not('end_date', 'is', null)
        .gte('end_date', monthsAgo.toISOString());

      const companyFlow = new Map();
      experiences?.forEach(exp => {
        if (!exp.companies) return;
        const key = exp.company_id;
        if (!companyFlow.has(key)) {
          companyFlow.set(key, { ...exp.companies, outbound_count: 0 });
        }
        companyFlow.get(key).outbound_count++;
      });

      setOutboundCompanies(
        Array.from(companyFlow.values())
          .filter(c => c.outbound_count >= 2)
          .sort((a, b) => b.outbound_count - a.outbound_count)
          .slice(0, 20)
      );

      // Get trending skills
      const { data: skillData } = await supabase
        .from('candidate_skills')
        .select('skill_id, skills(id, name, type)');

      const skillMap = new Map();
      skillData?.forEach(cs => {
        if (!cs.skills) return;
        if (!skillMap.has(cs.skill_id)) {
          skillMap.set(cs.skill_id, { ...cs.skills, count: 0 });
        }
        skillMap.get(cs.skill_id).count++;
      });

      setTrendingSkills(
        Array.from(skillMap.values())
          .sort((a, b) => b.count - a.count)
          .slice(0, 20)
      );
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setMovementLoading(false);
    }
  };

  const searchCompaniesWithSkill = async () => {
    if (!selectedSkill) return;
    setSkillLoading(true);
    try {
      const { data: candidateSkills } = await supabase
        .from('candidate_skills')
        .select('candidate_id, candidates!inner(id)')
        .eq('skill_id', selectedSkill);

      if (!candidateSkills?.length) {
        setSkillCompanies([]);
        return;
      }

      const candidateIds = candidateSkills.map(cs => cs.candidate_id);

      let query = supabase
        .from('candidate_experience')
        .select('company_id, candidate_id, job_title, companies!inner(id, name, location, category)')
        .in('candidate_id', candidateIds)
        .eq('is_current', true);

      if (skillLocation) {
        query = query.ilike('companies.location', `%${skillLocation}%`);
      }

      const { data: experiences } = await query;

      const companyMap = new Map();
      experiences?.forEach(exp => {
        const companyId = exp.company_id;
        if (!companyMap.has(companyId)) {
          companyMap.set(companyId, { ...exp.companies, candidate_count: 0 });
        }
        companyMap.get(companyId).candidate_count++;
      });

      setSkillCompanies(Array.from(companyMap.values()));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setSkillLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this company?')) return;
    try {
      const { error } = await supabase.from('companies').delete().eq('id', id);
      if (error) throw error;
      toast({ title: 'Company deleted successfully' });
      fetchCompanies();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('companies')
        .update({ crm_status: newStatus })
        .eq('id', id);
      if (error) throw error;
      toast({ title: 'Status updated' });
      fetchCompanies();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = 
      company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.location?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || company.crm_status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || company.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const filteredSkills = skillTypeFilter === 'all' 
    ? skills 
    : skills.filter(s => s.type === skillTypeFilter);

  const skillTypes = [...new Set(skills.map(s => s.type).filter(Boolean))];

  if (loading) {
    return (
      <RecruiterLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </RecruiterLayout>
    );
  }

  return (
    <RecruiterLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Companies</h1>
            <p className="text-muted-foreground">Manage companies, CRM status, and discover talent by skills</p>
          </div>
          {can.create('companies') && (
            <Button onClick={() => { setSelectedCompany(null); setFormOpen(true); }}>
              <Plus className="mr-2 h-4 w-4" />
              Add Company
            </Button>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="companies" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              All Companies
            </TabsTrigger>
            <TabsTrigger value="skill-search" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Search by Skill
            </TabsTrigger>
            <TabsTrigger value="talent-movement" className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Talent Movement
            </TabsTrigger>
          </TabsList>

          <TabsContent value="companies" className="space-y-4 mt-4">
            <Card className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search companies..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="CRM Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="not_prospect">Not Prospect</SelectItem>
                    <SelectItem value="lead">Lead</SelectItem>
                    <SelectItem value="prospect">Prospect</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <ViewToggle view={view} onViewChange={setView} />
              </div>
            </Card>

            {filteredCompanies.length === 0 ? (
              <Card className="p-12 text-center">
                <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No companies found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Add your first company to get started'}
                </p>
              </Card>
            ) : view === 'card' ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredCompanies.map(company => (
                  <Card
                    key={company.id}
                    className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(`/companies/${company.id}`)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Building2 className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold truncate">{company.name}</h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge className={CRM_STATUS_COLORS[company.crm_status || 'lead']} variant="outline">
                            {(company.crm_status || 'lead').replace('_', ' ')}
                          </Badge>
                          <Badge variant="secondary">
                            {CATEGORY_LABELS[company.category] || company.category}
                          </Badge>
                        </div>
                        {(company.primary_location || company.location) && (
                          <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {company.primary_location || company.location}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {company.candidate_count} candidates
                          </span>
                          {company.employee_count_estimate && (
                            <span className="flex items-center gap-1">
                              <TrendingUp className="h-3 w-3" />
                              ~{company.employee_count_estimate}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                          <Select
                            value={company.crm_status || 'lead'}
                            onValueChange={(value) => handleStatusChange(company.id, value)}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="not_prospect">Not Prospect</SelectItem>
                              <SelectItem value="lead">Lead</SelectItem>
                              <SelectItem value="prospect">Prospect</SelectItem>
                              <SelectItem value="client">Client</SelectItem>
                              <SelectItem value="on_hold">On Hold</SelectItem>
                              <SelectItem value="lost">Lost</SelectItem>
                            </SelectContent>
                          </Select>
                          {can.update('companies') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedCompany(company); setFormOpen(true); }}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                          )}
                          {can.delete('companies') && (
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(company.id)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Company</TableHead>
                      <TableHead>CRM Status</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Candidates</TableHead>
                      <TableHead>Links</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCompanies.map(company => (
                      <TableRow
                        key={company.id}
                        className="cursor-pointer"
                        onClick={() => navigate(`/companies/${company.id}`)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{company.name}</span>
                          </div>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Select
                            value={company.crm_status || 'lead'}
                            onValueChange={(value) => handleStatusChange(company.id, value)}
                          >
                            <SelectTrigger className="h-8 w-[120px]">
                              <Badge className={CRM_STATUS_COLORS[company.crm_status || 'lead']} variant="outline">
                                {(company.crm_status || 'lead').replace('_', ' ')}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="not_prospect">Not Prospect</SelectItem>
                              <SelectItem value="lead">Lead</SelectItem>
                              <SelectItem value="prospect">Prospect</SelectItem>
                              <SelectItem value="client">Client</SelectItem>
                              <SelectItem value="on_hold">On Hold</SelectItem>
                              <SelectItem value="lost">Lost</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {CATEGORY_LABELS[company.category] || company.category}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {(company.primary_location || company.location) && (
                            <span className="flex items-center gap-1 text-sm">
                              <MapPin className="h-3 w-3" />
                              {company.primary_location || company.location}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="flex items-center gap-1 text-sm">
                            <Users className="h-3 w-3" />
                            {company.candidate_count}
                          </span>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-2">
                            {company.website_url && (
                              <a href={company.website_url} target="_blank" rel="noopener noreferrer">
                                <Globe className="h-4 w-4 text-muted-foreground hover:text-primary" />
                              </a>
                            )}
                            {company.linkedin_url && (
                              <a href={company.linkedin_url} target="_blank" rel="noopener noreferrer">
                                <Linkedin className="h-4 w-4 text-muted-foreground hover:text-primary" />
                              </a>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-2">
                            {can.update('companies') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setSelectedCompany(company); setFormOpen(true); }}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                            )}
                            {can.delete('companies') && (
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(company.id)}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="skill-search" className="space-y-4 mt-4">
            {!selectedSkill && skillStats.length > 0 && (
              <Card className="p-6">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  Top Skills in Database
                </h3>
                <div className="flex flex-wrap gap-2">
                  {skillStats.map(skill => (
                    <Button 
                      key={skill.id} 
                      variant="outline" 
                      size="sm"
                      className="flex items-center gap-2"
                      onClick={() => setSelectedSkill(skill.id)}
                    >
                      {skill.name}
                      <Badge variant="secondary" className="text-xs">{skill.count}</Badge>
                    </Button>
                  ))}
                </div>
              </Card>
            )}

            <Card className="p-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Filter by Type</label>
                  <Select value={skillTypeFilter} onValueChange={setSkillTypeFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {skillTypes.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Select Skill</label>
                  <Select value={selectedSkill} onValueChange={setSelectedSkill}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a skill..." />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredSkills.map(skill => (
                        <SelectItem key={skill.id} value={skill.id}>
                          {skill.name} <span className="text-muted-foreground">({skill.type})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Filter by Location</label>
                  <Input
                    placeholder="e.g., New York, Remote..."
                    value={skillLocation}
                    onChange={(e) => setSkillLocation(e.target.value)}
                  />
                </div>
              </div>
            </Card>

            {skillLoading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : !selectedSkill ? (
              <Card className="p-12 text-center">
                <Search className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-xl font-semibold mb-2">Select a skill to begin</h3>
                <p className="text-muted-foreground">
                  Choose a skill from the dropdown or click on a top skill above
                </p>
              </Card>
            ) : skillCompanies.length === 0 ? (
              <Card className="p-12 text-center">
                <h3 className="text-xl font-semibold mb-2">No companies found</h3>
                <p className="text-muted-foreground">
                  No candidates with this skill are currently working at any company
                </p>
                <Button variant="outline" className="mt-4" onClick={() => setSelectedSkill('')}>
                  Clear Selection
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold">
                    Companies with {skills.find(s => s.id === selectedSkill)?.name} expertise
                  </h2>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{skillCompanies.length} companies</Badge>
                    <Button variant="outline" size="sm" onClick={() => setSelectedSkill('')}>
                      Clear
                    </Button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {skillCompanies.map(company => (
                    <Card 
                      key={company.id} 
                      className="p-6 hover:shadow-lg transition-shadow cursor-pointer group"
                      onClick={() => navigate(`/companies/${company.id}`)}
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <Building2 className="h-8 w-8 text-primary" />
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg group-hover:text-primary transition-colors">
                            {company.name}
                          </h3>
                          {company.location && (
                            <p className="text-sm text-muted-foreground">{company.location}</p>
                          )}
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                      <Badge className="mb-3" variant="outline">
                        {company.category?.replace('_', ' ')}
                      </Badge>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="h-4 w-4" />
                        <span>{company.candidate_count} candidate(s) with this skill</span>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="talent-movement" className="space-y-4 mt-4">
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">Talent Movement & Trends</h3>
                  <p className="text-sm text-muted-foreground">Market intelligence on talent flow</p>
                </div>
                <Select value={timeWindow} onValueChange={setTimeWindow}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">Last 3 months</SelectItem>
                    <SelectItem value="6">Last 6 months</SelectItem>
                    <SelectItem value="12">Last 12 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </Card>

            {movementLoading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingDown className="h-5 w-5 text-orange-500" />
                    <h2 className="text-lg font-semibold">Companies Losing Talent</h2>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    High employee outbound flow in selected period
                  </p>
                  {outboundCompanies.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No significant outbound flow detected</p>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {outboundCompanies.map(company => (
                        <Card 
                          key={company.id} 
                          className="p-3 hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => navigate(`/companies/${company.id}`)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-5 w-5 text-muted-foreground" />
                              <div>
                                <h3 className="font-medium text-sm">{company.name}</h3>
                                {company.location && (
                                  <p className="text-xs text-muted-foreground">{company.location}</p>
                                )}
                              </div>
                            </div>
                            <Badge variant="destructive" className="text-xs">
                              {company.outbound_count} left
                            </Badge>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </Card>

                <Card className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="h-5 w-5 text-primary" />
                    <h2 className="text-lg font-semibold">Most In-Demand Skills</h2>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Skills most frequently in candidate profiles
                  </p>
                  {trendingSkills.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No skill data available</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 max-h-[400px] overflow-y-auto">
                      {trendingSkills.map((skill, index) => (
                        <Card key={skill.id} className="p-3">
                          <div className="flex items-start justify-between mb-1">
                            <Badge variant="outline" className="text-xs">#{index + 1}</Badge>
                            <Badge variant="secondary" className="text-xs">{skill.type}</Badge>
                          </div>
                          <h3 className="font-medium text-sm">{skill.name}</h3>
                          <p className="text-xs text-muted-foreground">{skill.count} candidates</p>
                        </Card>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <CompanyForm
        company={selectedCompany}
        open={formOpen}
        onOpenChange={setFormOpen}
        onSuccess={() => {
          fetchCompanies();
          setFormOpen(false);
        }}
      />
    </RecruiterLayout>
  );
};

export default CompanyManagement;
